import {
  AgentDelegationKind,
  AgentDelegationTarget,
  AgentDelegationTaskType,
  LocalCodingAgentRuntime,
} from '@this+that/models';

import type { DelegateInboxEntryToAgentInput } from '../../services/inboxEntries.service';

/** Aider has no IMPLEMENT_AND_PR mode — forcing TECHNICAL_ANALYSIS is how
 *  "Plan first" stays checked-and-disabled for it everywhere this runs
 *  (DoBoxDelegateMenuTaskTypeStep's run-mode cards, the composer's
 *  checkbox). */
export function resolveTaskTypeForRuntime(
  runtime: LocalCodingAgentRuntime,
  taskType: AgentDelegationTaskType,
): AgentDelegationTaskType {
  if (
    runtime === LocalCodingAgentRuntime.AIDER &&
    taskType === AgentDelegationTaskType.IMPLEMENT_AND_PR
  ) {
    return AgentDelegationTaskType.TECHNICAL_ANALYSIS;
  }
  return taskType;
}

export function resolveDelegationRuntime(
  target: AgentDelegationTarget,
  selectedRuntime: LocalCodingAgentRuntime,
): LocalCodingAgentRuntime {
  const availableRuntimes = target.availableLocalRuntimes;
  if (!availableRuntimes?.length) {
    return selectedRuntime;
  }
  return availableRuntimes.includes(selectedRuntime)
    ? selectedRuntime
    : availableRuntimes[0];
}

export interface BuildDelegationInputVariablesParams {
  target: AgentDelegationTarget;
  taskType: AgentDelegationTaskType;
  selectedLocalRuntime: LocalCodingAgentRuntime;
  selectedLocalModel: string;
  selectedAgentProfile: string;
  instructions: string;
}

/**
 * Shared by the DoBoxDelegateMenu task-type step and the DoBox detail panel's
 * delegate composer (DoBoxDelegateComposer) — the single place that turns a
 * chosen AgentDelegationTarget + task type + local run config into the
 * DelegateInboxEntryToAgentInput the delegateInboxEntryToAgent mutation
 * expects. Never duplicate this mapping at a second call site.
 */
export function buildDelegationInputVariables({
  target,
  taskType,
  selectedLocalRuntime,
  selectedLocalModel,
  selectedAgentProfile,
  instructions,
}: BuildDelegationInputVariablesParams): DelegateInboxEntryToAgentInput {
  const resolvedRuntime =
    target.kind === AgentDelegationKind.LOCAL_MCP_CODING
      ? resolveDelegationRuntime(target, selectedLocalRuntime)
      : undefined;
  const resolvedTaskType = resolvedRuntime
    ? resolveTaskTypeForRuntime(resolvedRuntime, taskType)
    : taskType;
  return {
    taskType: resolvedTaskType,
    mcpConnectionId:
      target.kind === AgentDelegationKind.LOCAL_MCP_CODING
        ? target.id
        : undefined,
    localRuntime: resolvedRuntime,
    localModel:
      target.kind === AgentDelegationKind.LOCAL_MCP_CODING
        ? selectedLocalModel || undefined
        : undefined,
    localAgentProfile:
      target.kind === AgentDelegationKind.LOCAL_MCP_CODING
        ? selectedAgentProfile || undefined
        : undefined,
    agentTemplateId:
      target.kind === AgentDelegationKind.AGENT_RUN ? target.id : undefined,
    instructions: instructions.trim() || undefined,
  };
}
